import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WasabiImgPipe } from '../../../shared/pipes/wasabi-img.pipe';
import { ModalWindowComponent } from '../modal-window/modal-window.component';
import { BehaviorSubject } from 'rxjs';
import {
  AGMedia,
  AGMediaKeys,
} from '../../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/utils/ag-media.model';
import { MediaAction } from './ag-media-gallery.models';
import {
  DeleteMediaPayloadKeys,
  MediaSize,
} from '../../../../../../../ag-common-lib/projects/ag-common-lib/src/lib/models/functions/wasabi';
import { confirm } from 'devextreme/ui/dialog';
import { CloudFunctionsService } from '../../services/cloud-functions.service';
import { ToastrService } from 'ngx-toastr';
import { ChangeSourceType } from '@ag-common-lib/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

@Component({
  selector: 'ag-shr-media-gallery',
  templateUrl: './ag-media-gallery.component.html',
  styleUrls: ['./ag-media-gallery.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AgMediaGalleryComponent {
  @HostBinding('class') className = 'ag-media-gallery';
  @ViewChild('fullScreenImg', { static: true }) fullScreenImgModalComponent: ModalWindowComponent;
  @ViewChild('renamePopup', { static: true }) renamePopupModalCompolnent: ModalWindowComponent;

  @Input() multipleSelection: boolean = true;
  @Input() allowActions: boolean = true;
  @Input() set allowDelete(allowDelete: boolean) {
    this.editOptions.find(item => item.text === 'Delete').visible = allowDelete;
  }
  @Input() allowZoomIn: boolean = true;
  @Input() mediaItems: AGMedia[];
  @Input() set mediaActions(mediaActions: MediaAction[]) {
    this.editOptions.push(...mediaActions);
  }
  @Input() sourceType: ChangeSourceType | null = null;

  @Input() set allowRenameMedia(allow: boolean) {
    const renameOption = this.editOptions.find(item => item.text === 'Rename');
    if (allow && !renameOption) {
      this.editOptions.push({ icon: 'edit', text: 'Rename', actionFn: this.onRenameItemClicked.bind(this) });
    }
  }

  @Output() selectedItemsChange = new EventEmitter<AGMedia[]>();
  @Output() renameItemClick = new EventEmitter<AGMedia>();
  /**
   * @deprecated Use onDeleteMedia instead
   */
  @Output() itemDeleted = new EventEmitter<AGMedia>();
  @Input() onDeleteMedia: ((media: AGMedia) => Promise<unknown> | void) | null = null;
  @Output() onDownloadMedia: EventEmitter<string[]> = new EventEmitter();

  fullScreenImgUrl$ = new BehaviorSubject<string>('');
  isAllSelected = false;

  protected _selectedItems = new Set<AGMedia>();

  protected readonly MediaSize = MediaSize;
  protected readonly AGMediaKeys = AGMediaKeys;

  protected readonly editOptions: MediaAction[] = [
    { icon: 'agi-download', text: 'Download', actionFn: this.onDownloadItemClicked.bind(this) },
    {
      icon: 'agi-trash-alt',
      text: 'Delete',
      actionFn: this.onDeleteItemClicked.bind(this),
    },
  ];

  mediaNameSig = signal('');
  editingItemSig = signal<AGMedia | null>(null);
  isDeletingSig = signal(false);

  isMediaNameValidSig = computed(() => {
    const mediaName = this.mediaNameSig();
    const editingItem = this.editingItemSig();

    if (!mediaName || mediaName === editingItem?.[AGMediaKeys.fileName]) {
      return false;
    }

    return !this.mediaItems?.some(
      item =>
        item[AGMediaKeys.fileName].toLowerCase() === mediaName.toLowerCase() &&
        item[BaseModelKeys.dbId] !== editingItem?.[BaseModelKeys.dbId]
    );
  });


  constructor(
    private wasabiImgPipe: WasabiImgPipe,
    private cloudFunctionsService: CloudFunctionsService,
    private tosterService: ToastrService,
  ) {}

  toggleImageSelection = (image: AGMedia, event: Event) => {
    if (this._selectedItems.has(image)) {
      this._selectedItems.delete(image);
      this.selectedItemsChange.next(Array.from(this._selectedItems.values()));
      return;
    }

    if (!this.multipleSelection) {
      this._selectedItems.clear();
    }

    this._selectedItems.add(image);

    this.selectedItemsChange.next(Array.from(this._selectedItems.values()));
  };

  zoomImage(image: AGMedia) {
    this.fullScreenImgUrl$.next(image[AGMediaKeys.wasabiPath]);
    this.fullScreenImgModalComponent.showModal();
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  trackByMediaId = (_index: number, image: AGMedia): string => {
    return image?.[BaseModelKeys.dbId] ?? image?.[AGMediaKeys.wasabiPath] ?? String(_index);
  };

  selectAll() {
    this.isAllSelected = !this.isAllSelected;
    this._selectedItems = new Set<AGMedia>(this.isAllSelected ? [...this.mediaItems] : []);
    this.selectedItemsChange.next(Array.from(this._selectedItems.values()));
  }

  async onDeleteItemClicked(imageItem: AGMedia): Promise<void> {
    const isConfirmed = await confirm('<i>Are you sure you want to Delete the file?</i>', 'Confirm');
    if (!isConfirmed) {
      return;
    }

    this.isDeletingSig.set(true);
    let isFileRemoved = false;

    try {
      await this.cloudFunctionsService.deleteMedia({
        [DeleteMediaPayloadKeys.filePath]: imageItem[AGMediaKeys.wasabiPath],
      });
      isFileRemoved = true;
      this._selectedItems.delete(imageItem);

      if (this.onDeleteMedia) {
        await this.onDeleteMedia(imageItem);
        return;
      }

      this.itemDeleted.emit(imageItem);
    } catch (err) {
      console.log('e', err);
      if (!isFileRemoved) {
        this.tosterService.error('File Deleting Failed');
      }
    } finally {
      this.isDeletingSig.set(false);
    }
  }

  async downloadSelectedImages() {
    this.saveDownloadedMedia();
    if (this._selectedItems.size === 1) {
      const [head] = this._selectedItems;
      this.onDownloadItemClicked(head);
      return;
    }
    const zip = new JSZip();

    for (const image of this._selectedItems) {
      const file = await this.getBlob(image);
      zip.file(image.fileName, file);
    }

    // Generate and download the zip file
    zip.generateAsync({ type: 'blob' }).then(content => {
      saveAs(content, 'media.zip');
    });
  }

  async getBlob(image: AGMedia) {
    const url = this.wasabiImgPipe.transform(image.wasabiPath);
    return fetch(url).then(res => res.blob());
  }

  async onDownloadItemClicked(imageItem: AGMedia) {
    const blob = await this.getBlob(imageItem);
    saveAs(blob, imageItem[AGMediaKeys.fileName]);
    this.onDownloadMedia.emit([imageItem.wasabiPath]);
  }

  logAction(e, imageItem) {
    e.itemData.actionFn(imageItem);
  }

  private saveDownloadedMedia = () => {
    const timeStamp = new Date().toJSON().slice(0, 19); // to remove cache
    const imageWasabiPathItems = Array.from(this._selectedItems)?.map(({wasabiPath}) => {
      return `${wasabiPath}_${timeStamp}`;
    });
    this.onDownloadMedia.emit(imageWasabiPathItems);
  }

  renameMedia(): void {
    if (!this.mediaNameSig() || !this.isMediaNameValidSig()) {
      return;
    }

    this.renameItemClick.next({
      ...this.editingItemSig(),
      [AGMediaKeys.fileName]: this.mediaNameSig()
    });
    this.renamePopupModalCompolnent.hideModal();
  }

  clearRenamePopup(): void {
    this.editingItemSig.set(null);
    this.mediaNameSig.set('');
  }

  onRenameItemClicked(imageItem: AGMedia): void {
    this.mediaNameSig.set(imageItem[AGMediaKeys.fileName]);
    this.editingItemSig.set(imageItem);
    this.renamePopupModalCompolnent.showModal();
  }


  validateUniqueName = (params: any): boolean => {
    if (!this.mediaNameSig() || this.mediaNameSig() === this.editingItemSig()[AGMediaKeys.fileName]) {
      return true;
    }

    return !this.mediaItems?.some(
      item =>
        item[AGMediaKeys.fileName].toLowerCase() === this.mediaNameSig().toLowerCase() &&
        item[BaseModelKeys.dbId] !== this.editingItemSig()[BaseModelKeys.dbId],
    );
  };
}
